import Fuse from 'fuse.js';

// Minimum combined relevance score per meaningful word to appear in results.
// Each word contributes 0–1 (1 = perfect match). An item must average at least
// this much relevance across all query words to be shown.
const MIN_RELEVANCE_PER_WORD = 0.3;

function buildFuse(faqItems) {
  const docs = faqItems.map((item) => ({
    item,
    title: item.querySelector('.accordion-header')?.textContent || '',
    body: item.querySelector('.accordion-body')?.textContent || '',
  }));

  return new Fuse(docs, {
    keys: ['title', 'body'],
    threshold: 0.4,
    ignoreLocation: true,
    includeScore: true,
  });
}

function scoreItems(fuse, words, faqItems) {
  const scores = new Map();

  for (const word of words) {
    for (const result of fuse.search(word)) {
      const { item: faqItem } = result.item;
      const { score = 1 } = result;
      scores.set(faqItem, (scores.get(faqItem) || 0) + (1 - score));
    }
  }

  const minScore = words.length * MIN_RELEVANCE_PER_WORD;
  return new Set(faqItems.filter((faqItem) => (scores.get(faqItem) || 0) >= minScore));
}

function expandItem(item) {
  const collapseEl = item.querySelector('.accordion-collapse');
  const btn = item.querySelector('.accordion-button');
  if (collapseEl) collapseEl.classList.add('show');
  if (btn) {
    btn.classList.remove('collapsed');
    btn.setAttribute('aria-expanded', 'true');
  }
}

function resetAccordions(faqItems, faqsWrapper, collapseEls) {
  for (const item of faqItems) item.classList.remove('d-none');
  for (const wrapper of faqsWrapper) wrapper.classList.remove('d-none');
  for (const collapse of collapseEls) {
    collapse.classList.remove('show');
    const accordionId = collapse.closest('.accordion')?.id;
    if (accordionId) collapse.setAttribute('data-bs-parent', `#${accordionId}`);
  }

  for (const btn of document.querySelectorAll('.accordion-button')) {
    btn.classList.add('collapsed');
    btn.setAttribute('aria-expanded', 'false');
  }
}


export function liveFaqSearch({ searchInput, noFaqResult }) {
  if (!searchInput || !noFaqResult) return;

  const searchQuery = (searchInput.value || '').trim();
  const faqItems = [...document.querySelectorAll('.accordion-item')];
  const faqsWrapper = document.querySelectorAll('.accordion-section-item');
  const collapseEls = document.querySelectorAll('.accordion-collapse');

  if (!searchQuery) {
    resetAccordions(faqItems, faqsWrapper, collapseEls);
    noFaqResult.classList.add('d-none');
    return;
  }

  const fuse = buildFuse(faqItems);
  const words = searchQuery.split(/\s+/).filter((w) => w.length >= 3);
  const matchedItems = scoreItems(fuse, words, faqItems);

  // Remove data-bs-parent so multiple items can stay open at once during search
  // without Bootstrap closing siblings when the user manually opens one
  for (const collapse of collapseEls) collapse.removeAttribute('data-bs-parent');

  for (const item of faqItems) {
    const matches = matchedItems.has(item);
    item.classList.toggle('d-none', !matches);
    if (matches) expandItem(item);
  }

  noFaqResult.classList.toggle('d-none', matchedItems.size !== 0);

  for (const wrapper of faqsWrapper) {
    const items = [...wrapper.querySelectorAll('.accordion-item')];
    const visibleItems = items.filter((el) => !el.classList.contains('d-none'));
    wrapper.classList.toggle('d-none', visibleItems.length === 0);
  }
}

export function initFaqSearchBar({ searchInput, searchForm, onSearch }) {
  if (!searchInput || !searchForm) return;

  let typingTimer;

  searchInput.addEventListener('keyup', (e) => {
    e.preventDefault();
    clearTimeout(typingTimer);
    typingTimer = setTimeout(onSearch, 500);
  });

  searchForm.addEventListener('submit', (e) => e.preventDefault());
}
